import { useList, useDelete, useNavigation, useOne, useCreate, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowLeft, Save, Loader2 } from 'lucide-react';
import type { OutboundPackage } from '@natsepa/database';
import { ImageManager, type ManagedImage } from '../../components/ImageManager';
import { BilingualField } from '../../components/BilingualField';
import { BilingualArrayField } from '../../components/BilingualArrayField';

function generateSlug(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export function OutboundPackagesList() {
  const { data, isLoading, error } = useList<OutboundPackage>({ resource: 'outbound-packages' });
  const { mutate: deleteOne } = useDelete();
  const { edit, create } = useNavigation();

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this outbound package?')) {
      deleteOne({ resource: 'outbound-packages', id });
    }
  };

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (error) return (
    <div className="p-4"><div className="bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-800">Error loading outbound packages: {error.message}</p></div></div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Outbound Packages</h1>
        <button onClick={() => create('outbound-packages')} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors">
          <Plus className="w-5 h-5" /> Add Outbound Package
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min. People</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.data.map((pkg) => (
              <tr key={pkg.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{pkg.name}</div>
                  <div className="text-sm text-gray-500">{pkg.slug}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{pkg.duration || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{pkg.minPeople} people</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{pkg.price ? `IDR ${pkg.price.toLocaleString()}` : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => edit('outbound-packages', pkg.id)} className="p-2 text-gray-400 hover:text-gold-600 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(pkg.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.data.length === 0 && <div className="p-8 text-center text-gray-500">No outbound packages found. Click "Add Outbound Package" to create one.</div>}
      </div>
    </div>
  );
}

export function OutboundPackagesCreate() {
  const navigate = useNavigate();
  const { mutate: createPkg, isPending } = useCreate();
  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    duration: '',
    price: 0,
    minPeople: 2,
    includes: [] as { en: string; id: string }[],
    itinerary: [] as { en: string; id: string }[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nameId) { alert('Please fill in the name'); return; }
    const slug = form.slug || generateSlug(form.nameId);
    createPkg(
      { resource: 'outbound-packages', values: { ...form, slug, name: form.nameId || form.nameEn, description: form.descriptionId || form.descriptionEn } },
      { onSuccess: (data) => { const newId = data?.data?.id; navigate(newId ? `/outbound-packages/edit/${newId}` : '/outbound-packages'); } }
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/outbound-packages')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Outbound Package</h1>
          <p className="text-sm text-gray-500 mt-1">Add a new outbound team-building or excursion package</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
          <BilingualField label="Name" valueEn={form.nameEn} valueId={form.nameId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })} required />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder={form.nameId ? generateSlug(form.nameId) : 'auto-generated'} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Package Details</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <input type="text" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g., 4 hours" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (IDR)</label>
              <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min. People</label>
              <input type="number" value={form.minPeople} onChange={(e) => setForm({ ...form, minPeople: parseInt(e.target.value) || 2 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800"><strong>Note:</strong> You can add images and itinerary details after creating the package.</p>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/outbound-packages')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Create & Continue
          </button>
        </div>
      </form>
    </div>
  );
}

export function OutboundPackagesEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useOne<OutboundPackage>({ resource: 'outbound-packages', id: id! });
  const { mutate: updatePkg, isPending } = useUpdate();

  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    duration: '',
    price: 0,
    minPeople: 2,
    includes: [] as { en: string; id: string }[],
    itinerary: [] as { en: string; id: string }[],
    highlights: [] as string[],
  });
  const [images, setImages] = useState<ManagedImage[]>([]);
  const [newHighlight, setNewHighlight] = useState('');

  useEffect(() => {
    if (data?.data) {
      const pkg = data.data;
      const zipBi = (en: any[] | undefined, id: any[] | undefined) => {
        const e = Array.isArray(en) ? en : [];
        const d = Array.isArray(id) ? id : [];
        const len = Math.max(e.length, d.length);
        return Array.from({ length: len }, (_, i) => ({ en: e[i] || d[i] || '', id: d[i] || e[i] || '' }));
      };
      setForm({
        slug: pkg.slug || '',
        nameEn: (pkg as any).nameEn || (pkg as any).name_en || pkg.name || '',
        nameId: (pkg as any).nameId || (pkg as any).name_id || pkg.name || '',
        descriptionEn: (pkg as any).descriptionEn || (pkg as any).description_en || pkg.description || '',
        descriptionId: (pkg as any).descriptionId || (pkg as any).description_id || pkg.description || '',
        shortDescriptionEn: (pkg as any).shortDescriptionEn || (pkg as any).short_description_en || pkg.shortDescription || '',
        shortDescriptionId: (pkg as any).shortDescriptionId || (pkg as any).short_description_id || pkg.shortDescription || '',
        duration: pkg.duration || '',
        price: pkg.price || 0,
        minPeople: pkg.minPeople || 2,
        includes: zipBi((pkg as any).includesEn, (pkg as any).includesId),
        itinerary: zipBi((pkg as any).itineraryEn, (pkg as any).itineraryId),
        highlights: pkg.highlights || [],
      });
      if (pkg.images) {
        setImages(pkg.images.map((url, index) => ({ url, imageType: 'gallery' as const, sortOrder: index })));
      }
    }
  }, [data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updatePkg(
      { resource: 'outbound-packages', id: id!, values: { ...form, images: images.map((img) => img.url) } },
      { onSuccess: () => navigate('/outbound-packages') }
    );
  };

  const addHighlight = () => {
    if (newHighlight.trim()) {
      setForm({ ...form, highlights: [...form.highlights, newHighlight.trim()] });
      setNewHighlight('');
    }
  };

  const removeHighlight = (index: number) => {
    setForm({ ...form, highlights: form.highlights.filter((_, i) => i !== index) });
  };

  if (isLoading) return <div className="p-4">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/outbound-packages')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Outbound Package</h1>
          <p className="text-sm text-gray-500 mt-1">{form.nameId || form.nameEn || 'Outbound package details'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
            <BilingualField label="Name" valueEn={form.nameEn} valueId={form.nameId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })} required />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input type="text" value={form.slug} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-gray-50" readOnly />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Description</h2>
            <BilingualField label="Short Description" valueEn={form.shortDescriptionEn} valueId={form.shortDescriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'shortDescriptionEn' : 'shortDescriptionId']: value })} multiline rows={2} />
            <BilingualField label="Full Description" valueEn={form.descriptionEn} valueId={form.descriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'descriptionEn' : 'descriptionId']: value })} multiline rows={4} />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Package Details</h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                <input type="text" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g., 4 hours" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (IDR)</label>
                <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min. People</label>
                <input type="number" value={form.minPeople} onChange={(e) => setForm({ ...form, minPeople: parseInt(e.target.value) || 2 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">What's Included</h2>
            <BilingualArrayField label="Includes" items={form.includes} onChange={(includes) => setForm({ ...form, includes })} />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Itinerary</h2>
            <BilingualArrayField label="Itinerary" items={form.itinerary} onChange={(itinerary) => setForm({ ...form, itinerary })} />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Highlights</h2>
            <div className="flex gap-2 mb-2">
              <input type="text" value={newHighlight} onChange={(e) => setNewHighlight(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHighlight())} placeholder="Add highlight..." className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" />
              <button type="button" onClick={addHighlight} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.highlights.map((item, index) => (
                <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-sm">
                  {item}
                  <button type="button" onClick={() => removeHighlight(index)} className="text-yellow-600 hover:text-yellow-800">x</button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate('/outbound-packages')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
              {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Changes
            </button>
          </div>
        </form>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Package Images</h2>
            <ImageManager entityType="outbound-package" entityId={id!} images={images} imageType="gallery" multiple={true} maxImages={10} onChange={setImages} />
          </div>
        </div>
      </div>
    </div>
  );
}
